import { microsoft365ToolName } from '@dbrain/shared';
import { Icons } from '@dbrain/ui';
import { useEffect, useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { failureAttempts, replyFailureView, shownDuration } from '@dbrain/app-core/reply-failure';
import { MANY_SKILLS, turnEnding, type CurrentStep, type StepDetails, type StepView, type TurnSteps } from '@dbrain/app-core/steps';

/**
 * The steps line of a turn (docs/design/mockups/Adimlar.dc.html). Running:
 * one line with only the current step, the elapsed time and a small arrow;
 * the arrow opens the finished steps with the current one at the bottom.
 * Done: a summary line ("14 sn'de hazırlandı · 2 arama · 4 sayfa", D-77);
 * it opens to every step with its time, each with its own arrow for its
 * details. Both start closed; a list the reader opened stays open when the
 * turn ends, so the answer below does not move. Skill loads and document
 * searches are steps here, counted in the summary; a stopped turn says so
 * in grey ("Durduruldu · 1 dk 50 sn"), not with the tick of a done one.
 */

/** Ticks while something runs, for the elapsed time. */
function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [active]);
  return now;
}

export function useDuration() {
  const { t, i18n } = useTranslation();
  const one = new Intl.NumberFormat(i18n.language, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  /** a turn: "14 sn", "1 dk 5 sn"; a whole minute is "1 dk", never "1 dk 0 sn" (16c.5, D-409) */
  const total = (ms: number): string => {
    const seconds = Math.max(0, Math.round(ms / 1000));
    if (seconds >= 60) {
      const minutes = Math.floor(seconds / 60);
      return seconds % 60 === 0 ? t('steps.time.minutesOnly', { minutes }) : t('steps.time.minutes', { minutes, seconds: seconds % 60 });
    }
    return t('steps.time.seconds', { value: String(seconds) });
  };
  return {
    /** a step: "2,1 sn"; from a minute on as a turn's time */
    step(ms: number): string {
      if (ms >= 60_000) return total(ms);
      return t('steps.time.seconds', { value: one.format(Math.max(ms, 0) / 1000) });
    },
    total,
  };
}

const QUOTED = new Set(['web_search', 'doc_search', 'skill_search']);

/**
 * "Web'de arandı:" and its subject; tools without their own words use their plain-language title. A built-in
 * tool's outcome from the server ("Gelen kutusu okundu · 24 mail", Phase 15e) names it once it is done.
 */
function useStepLabel() {
  const { t } = useTranslation();
  return (step: StepView, phase: 'running' | 'done'): { verb: string; subject: string } => {
    if (step.kind === 'think') return { verb: t(`steps.think.${step.phase ?? 'examine'}`), subject: '' };
    if (step.kind === 'answer') return { verb: t('steps.answer'), subject: '' };
    if (phase === 'done' && step.status === 'done' && step.label) return { verb: '', subject: step.label };
    const tool = step.tool ?? '';
    if (tool === 'run_code' || !t(`steps.tools.${tool}.${phase}`, { defaultValue: '' })) return { verb: '', subject: step.subject || step.title };
    const subject = phase === 'done' && QUOTED.has(tool) && step.subject ? `"${step.subject}"` : step.subject;
    return { verb: t(`steps.tools.${tool}.${phase}`), subject };
  };
}

function Label({ verb, subject, muteVerb = false }: { verb: string; subject: string; muteVerb?: boolean }) {
  if (!verb) return <>{subject}</>;
  if (!subject) return <>{verb}</>;
  return (
    <>
      {muteVerb ? <span className="steps__verb">{verb}</span> : verb} {subject}
    </>
  );
}

function CurrentLabel({ current }: { current: CurrentStep }) {
  const { t } = useTranslation();
  const label = useStepLabel();
  switch (current.kind) {
    case 'tool': {
      const { verb, subject } = label(current.step, 'running');
      return (
        <>
          <Label verb={verb} subject={subject} muteVerb />
          {current.more > 0 ? <span className="steps__more"> +{current.more}</span> : null}
        </>
      );
    }
    case 'retry':
      return <>{t('steps.current.retry', { attempt: current.attempt, max: current.max })}</>;
    case 'thinking':
      return <>{t(`steps.current.${current.phase}`)}</>;
    case 'waiting':
      return <>{t(current.for === 'answer' ? 'steps.current.waitingAnswer' : 'steps.current.waiting')}</>;
    default:
      return <>{t(`steps.current.${current.kind}`)}</>;
  }
}

function Spinner() {
  return <span className="ui-spinner steps__spinner" aria-hidden="true" />;
}

function Details({ details, profile }: { details: StepDetails; profile: 'chat' | 'code' }) {
  const { t } = useTranslation();
  switch (details.kind) {
    case 'skill': {
      const lines = [
        details.byUser ? t('steps.skill.byUser') : null,
        details.reused ? t('steps.skill.reused') : null,
        details.parts > 1 ? t('steps.skill.part', { part: details.part, parts: details.parts }) : null,
        // what runs without asking matters where commands are seen (D-35)
        profile === 'code' && details.grants.length > 0 ? t('steps.skill.grants', { list: details.grants.map((g) => (g === '*' ? t('steps.skill.ownScripts') : g)).join('; ') }) : null,
      ].filter(Boolean);
      return lines.length > 0 ? <div className="step__details step__text">{lines.join(' · ')}</div> : null;
    }
    case 'results':
      return (
        <ul className="step__details step__results" aria-label={t('steps.results')}>
          {details.results.map((hit) => (
            <li key={hit.url}>
              <span className="step__host">{hit.host}</span>
              <a href={hit.url} target="_blank" rel="noreferrer noopener">
                {hit.title}
              </a>
            </li>
          ))}
          {details.more > 0 ? <li className="step__host">{t('steps.moreResults', { count: details.more })}</li> : null}
        </ul>
      );
    case 'page':
      return (
        <div className="step__details step__page">
          <a href={details.url} target="_blank" rel="noreferrer noopener">
            {details.title ?? details.url}
          </a>
          {details.excerpt ? <p>{details.excerpt}</p> : null}
        </div>
      );
    case 'thought':
      return (
        <div className="step__details step__thought" role="group" aria-label={t('steps.thought')}>
          {details.text}
        </div>
      );
    default:
      return <div className="step__details step__text">{details.text}</div>;
  }
}

function StepRow({ step, profile }: { step: StepView; profile: 'chat' | 'code' }) {
  const { t } = useTranslation();
  const label = useStepLabel();
  const duration = useDuration();
  const [open, setOpen] = useState(false);
  const id = useId();
  // 16c.5 (D-409): a step's time keeps its tenth ("0,4 sn"), never "0,0 sn"
  const time = step.durationMs !== null && step.durationMs >= 50 ? duration.step(step.durationMs) : '';
  if (step.unreadable || step.status === 'failed') {
    const reason = t(`steps.reasons.${step.unreadable ?? 'other'}`);
    const text =
      step.kind === 'read'
        ? step.count > 1
          ? t('steps.unreadable.many', { count: step.count, reason })
          : t('steps.unreadable.one', { host: step.subject, reason })
        : step.kind === 'prepare'
          ? t('steps.docWaitFailed', { names: step.subject })
          : `${(() => {
            const { verb, subject } = label(step, 'done');
            return verb ? `${verb} ${subject}` : subject;
          })()} · ${t('steps.failed')}`;
    return (
      <li className="step step--quiet" data-testid="step" data-kind={step.kind} data-status="failed">
        <Icons.skipped size={16} className="step__icon" />
        <span className="step__label">{text}</span>
        <span className="step__time">{time}</span>
      </li>
    );
  }
  const { verb, subject } = label(step, 'done');
  // D-179: a connection's tool that ran without asking says why (OnayKartiAyrinti.dc.html)
  const allowed = step.allowedBy ? (
    <span className="step__note" data-testid="step-allowed-by">
      {' · '}
      {t(`steps.allowedBy.${step.allowedBy}`)}
    </span>
  ) : null;
  return (
    <li className={open ? 'step step--open' : 'step'} data-testid="step" data-kind={step.kind} data-status={step.status}>
      <div className="step__row">
        <Icons.done size={16} className="step__icon step__icon--done" />
        {step.details ? (
          <button type="button" className="step__toggle" aria-expanded={open} aria-controls={open ? id : undefined} onClick={() => setOpen(!open)} data-testid="step-toggle">
            <span className="step__label">
              <Label verb={verb} subject={subject} />
              {allowed}
            </span>
            {open ? <Icons.chevronDown size={13} /> : <Icons.chevronRight size={13} />}
          </button>
        ) : (
          <span className="step__label">
            <Label verb={verb} subject={subject} />
            {allowed}
          </span>
        )}
        <span className="step__time">{time}</span>
      </div>
      {open && step.details ? (
        <div id={id}>
          <Details details={step.details} profile={profile} />
        </div>
      ) : null}
    </li>
  );
}

/** `allowance`: plans decide the daily allowance (Phase 16a), so a used-up one waits instead of "Bütçe dolduğu için durdu" */
export function Steps({ turn, usage, profile = 'chat', allowance = false }: { turn: TurnSteps; usage?: ReactNode; profile?: 'chat' | 'code'; allowance?: boolean }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const listId = useId();
  const now = useNow(turn.running);
  const duration = useDuration();
  const started = turn.startedAt ? Date.parse(turn.startedAt) : null;
  const elapsed = started !== null ? (turn.running ? now : turn.completedAt ? Date.parse(turn.completedAt) : now) - started : null;
  const s = turn.summary;
  const parts = [
    s.skills > 0 ? t('steps.parts.skills', { count: s.skills }) : null,
    s.docs > 0 ? t('steps.parts.docs', { count: s.docs }) : null,
    s.searches > 0 ? t('steps.parts.searches', { count: s.searches }) : null,
    s.pages > 0 ? t('steps.parts.pages', { count: s.pages }) : null,
    s.files > 0 ? t('steps.parts.files', { count: s.files }) : null,
    s.commands > 0 ? t('steps.parts.commands', { count: s.commands }) : null,
    s.other > 0 ? t('steps.parts.other', { count: s.other }) : null,
  ].filter(Boolean);
  // "14 sn'de hazırlandı"; a turn that stopped early was not prepared (D-77): "Durduruldu · 1 dk 50 sn"
  const ending = turnEnding(turn.reason, turn.errorCode);
  // 16c.1, 16c.5 (D-409): no turn says a time under a second, however it ended ("Durduruldu", never "Durduruldu · 0 sn")
  const shown = shownDuration(elapsed);
  const time = shown !== null ? duration.total(shown) : null;
  const said = ending === 'budget' && allowance ? 'allowance' : ending;
  const outcome = time ? t(`steps.outcome.${said}`, { time }) : t(`steps.outcome.${said}NoTime`);
  // Phase 15e: a turn that only read Microsoft 365 says what it read: "Mail bulundu · ek okundu: … · 2,8 sn"
  // 16b.1 (HataUzunDosya): a turn that got no answer says why after its time: "Tamamlanamadı · 4 dk 38 sn · dosya yarıda kaldı"
  const failure = turn.failure && turn.errorCode === 'no_visible_reply' ? turn.failure : null;
  const reason = failure ? replyFailureView({ failure, locale: i18n.language, profile, time: null, model: null }).chipReason : null;
  const tries = failure ? failureAttempts(failure, i18n.language) : [];
  const labels =
    ending === 'prepared' && turn.labels
      ? [...turn.labels.map((label, n) => (n === 0 ? label : label.charAt(0).toLocaleLowerCase(i18n.language) + label.slice(1))), ...(shown !== null ? [duration.step(shown)] : [])]
      : null;
  const summary = labels ? labels.join(' · ') : [outcome, ...(reason ? [reason] : []), ...parts].join(' · ');
  // part 3 (MsSohbetOnay): while only its Microsoft 365 write card waits, the line says what was done and what the
  // card holds, with the tick: "Outlook'ta arandı · 1 mail bulundu · taslak yazıldı"
  const prepared =
    turn.running && turn.current?.kind === 'waiting' && turn.prepared && turn.labels
      ? [...turn.labels, t(`steps.tools.${microsoft365ToolName(turn.prepared)}.prepared`)]
          .map((label, n) => (n === 0 ? label : label.charAt(0).toLocaleLowerCase(i18n.language) + label.slice(1)))
          .join(' · ')
      : null;
  // a failed turn opens to its tries even when no step finished (16b.1)
  const canOpen = turn.running || turn.steps.length > 0 || tries.length > 0;
  return (
    <div className="steps" data-testid="steps" data-state={turn.running ? 'running' : 'done'} data-run={turn.runId}>
      <button
        type="button"
        className={['steps__line', open ? 'steps__line--open' : '', turn.running && turn.current?.kind === 'waiting' && !prepared ? 'steps__line--waiting' : ''].join(' ').trim()}
        aria-expanded={canOpen ? open : undefined}
        aria-controls={canOpen && open ? listId : undefined}
        aria-label={turn.running ? undefined : `${summary}. ${open ? t('steps.hide') : t('steps.show')}`}
        onClick={() => canOpen && setOpen(!open)}
        data-testid="steps-line"
      >
        {prepared ? (
          <>
            <Icons.done size={16} className="steps__ok" />
            <span className="steps__label steps__label--summary" aria-live="polite" data-testid="steps-prepared">
              {prepared}
            </span>
          </>
        ) : turn.running ? (
          turn.current?.kind === 'waiting' ? (
            turn.current.for === 'answer' ? (
              <Icons.time size={16} className="steps__waiting" />
            ) : (
              <Icons.warning size={16} className="steps__waiting" />
            )
          ) : (
            <Spinner />
          )
        ) : ending === 'prepared' ? (
          <Icons.done size={16} className="steps__ok" />
        ) : (
          <Icons.stop size={14} className="steps__ended" data-testid="steps-ended" />
        )}
        {prepared ? null : turn.running && turn.current ? (
          <>
            <span className="steps__label" aria-live="polite" data-testid="steps-current">
              <CurrentLabel current={turn.current} />
            </span>
            {shown !== null ? <span className="steps__time">· {duration.total(shown)}</span> : null}
          </>
        ) : (
          <span className="steps__label steps__label--summary" data-testid="steps-summary">
            {summary}
          </span>
        )}
        {canOpen ? open ? <Icons.chevronUp size={14} /> : <Icons.chevronDown size={14} /> : null}
      </button>
      {open && canOpen ? (
        <ol className="steps__list" id={listId} aria-label={t('steps.label')} data-testid="steps-list">
          {turn.steps.map((step) => (
            <StepRow key={step.id} step={step} profile={profile} />
          ))}
          {!turn.running
            ? tries.map((attempt, n) => (
                <li key={`try:${n}`} className="step step--quiet" data-testid="step-try">
                  <Icons.skipped size={16} className="step__icon" />
                  <span className="step__label">{attempt.label}</span>
                  {/* 16c.1: no time under a second */}
                  {attempt.ms >= 1000 ? <span className="step__time">{duration.step(attempt.ms)}</span> : null}
                </li>
              ))
            : null}
          {s.skills > MANY_SKILLS ? (
            <li className="step step--note" data-testid="steps-many-skills">
              <Icons.warning size={16} className="step__icon" />
              <span className="step__label">{t('steps.manySkills', { count: s.skills })}</span>
            </li>
          ) : null}
          {turn.running && turn.current ? (
            <li className="step step--current" data-testid="step-current">
              <div className="step__row">
                {turn.current.kind === 'waiting' ? <Icons.warning size={16} className="step__icon steps__waiting" /> : <Spinner />}
                <span className="step__label">
                  <CurrentLabel current={turn.current} />
                </span>
                <span className="step__time">…</span>
              </div>
            </li>
          ) : null}
          {!turn.running && usage ? <li className="steps__usage">{usage}</li> : null}
        </ol>
      ) : null}
    </div>
  );
}
